// Plated tab (Miami / Phoenix / Chicago): what a decision changes downstream, for the update pop-up.
// Pure functions over the Havn OS production feed so they can be tested without a browser.

export const PLATED_CITIES = ['miami', 'phoenix', 'chicago']

export const HOLD_LABEL = {
  custom: 'Has a customization',
  over_target: 'Past the plated count',
  manual: 'Manual approval is on',
  change: 'Change to an order in production',
}

const sumBy = (lines, pick) => lines.reduce((n, line) => n + pick(line), 0)

/** Per-dish quantity of a set of lines, keyed by dish name. */
function byDish(lines) {
  const out = new Map()
  for (const line of lines) out.set(line.name, (out.get(line.name) || 0) + line.qty)
  return out
}

/**
 * What approving (or approving without customizations) a waiting order or change does to the kitchen's numbers.
 * Returns rows for the pop-up: { area, detail } — meal counts, custom plates, labels, packing, routes.
 */
export function approvalEffects(order, { withoutCustom = false, routedOrders = 0 } = {}) {
  const change = order.pendingChange
  const after = change ? change.lines : order.lines
  const before = change ? order.lines : []
  const container = change ? change.container : order.container
  const rows = []

  const dishes = new Set([...byDish(before).keys(), ...byDish(after).keys()])
  const was = byDish(before)
  const now = byDish(after)
  const moves = [...dishes]
    .map(name => ({ name, delta: (now.get(name) || 0) - (was.get(name) || 0) }))
    .filter(m => m.delta !== 0)
  const containerNote = change && change.container !== order.container ? ` (container ${order.container} → ${change.container})` : ''
  rows.push({
    area: 'Meal counts',
    detail: moves.length
      ? moves.map(m => `${m.name} ${m.delta > 0 ? '+' : ''}${m.delta} ${container}`).join(', ') + containerNote
      : `No dish totals change${containerNote}`,
  })
  if (order.fits === false) rows.push({ area: 'Plated count', detail: 'Goes past what was plated; those meals need plating too' })

  const customAfter = withoutCustom ? 0 : sumBy(after, l => (l.customization ? l.qty : 0))
  const customBefore = sumBy(before, l => (l.customization ? l.qty : 0))
  const customDelta = customAfter - customBefore
  rows.push({
    area: 'Custom plates',
    detail: customDelta
      ? `${customDelta > 0 ? '+' : ''}${customDelta} custom meal${Math.abs(customDelta) === 1 ? '' : 's'} on Customs`
      : withoutCustom ? 'Made as standard; the customer is told the customization was dropped' : 'No change',
  })
  rows.push({
    area: 'Labels',
    detail: customDelta > 0 ? `${customDelta} more customer label${customDelta === 1 ? '' : 's'} to print` : customDelta < 0 ? `${-customDelta} label${customDelta === -1 ? '' : 's'} no longer needed` : 'No change',
  })
  rows.push({ area: 'Packing', detail: change ? 'Repack this order from the new list' : 'One more order to pack' })
  rows.push({
    area: 'Routes',
    detail: order.routing
      ? `Already on ${order.routing.route}, stop ${order.routing.stop}; the driver app shows it as changed`
      : routedOrders > 0 ? 'Needs a stop: routes are already built this week' : 'Routes not built yet; nothing to do',
  })
  return rows
}

/** What declining does: nothing the kitchen makes changes; the customer is told. */
export function declineEffects(order) {
  if (order.pendingChange) {
    return [
      { area: 'Kitchen', detail: 'Nothing changes: the order stays as it was admitted' },
      { area: 'Customer', detail: 'Told the change could not be made this week' },
    ]
  }
  return [
    { area: 'Kitchen', detail: 'Not made; never counted' },
    { area: 'Customer', detail: order.paid ? 'Told it is declined; what they paid is credited toward next week' : 'Told it is declined; any payment ask stops' },
  ]
}

/** Meal-count rows for a refresh of orders already in production (counts moved since the last import). */
export function importEffects(countsSinceImport = []) {
  return countsSinceImport.map(c => ({ area: c.name, detail: `${c.imported} → ${c.now}` }))
}

/** Waiting orders and changes, oldest first. */
export function waitingOrders(feed) {
  return (feed?.orders || [])
    .filter(o => o.kitchenStatus === 'pending' || o.pendingChange)
    .sort((a, b) => (a.pendingChange?.requestedAt || '').localeCompare(b.pendingChange?.requestedAt || '') || a.id.localeCompare(b.id))
}

/** The limit a dish is held to (plated once entered, else the cook target), by container. */
export function dishRows(feed) {
  const lock = feed?.lock
  const target = new Map((lock?.target || []).map(t => [t.itemId, t]))
  const plated = new Map((lock?.plated || []).map(p => [p.itemId, p]))
  return (feed?.dishes || []).map(d => ({
    ...d,
    target: target.get(d.itemId) || null,
    plated: plated.get(d.itemId) || null,
  }))
}
